import { EditRights, Roles, type User, type UserMarket } from '@cdab/scania/qpr/schema'
import { enumToTranslationText } from '@cdab/utils'
import type { TFunction } from 'react-i18next'

export type PreparedUserData = {
  name: string
  userName: string
  email: string
  market: string
  role: string
  editRights: string
  status: string
  userId: number
}

function getUserMarketsString(
  markets: UserMarket[],
  defaultMarketId: User['defaultMarketId']
) {
  if (markets.length === 1) return markets[0].name

  const MAX_MARKETS_TO_DISPLAY = 3

  const defaultMarket = markets.find(
    market => market.id === defaultMarketId
  )?.name

  let displayedMarkets = markets
    .filter(market => market.id !== defaultMarketId)
    .slice(
      0,
      defaultMarket ? MAX_MARKETS_TO_DISPLAY - 1 : MAX_MARKETS_TO_DISPLAY
    )
    .map(market => market.name)
    .join(', ')

  displayedMarkets =
    markets.length > 3 ? displayedMarkets + '...' : displayedMarkets

  return defaultMarket
    ? `${defaultMarket + ', '}${displayedMarkets}`
    : `${displayedMarkets} `
}

export function prepareTableData(
  data: User[],
  t: TFunction
): PreparedUserData[] {
  const preparedData = data
    .sort((aUser: User, bUser: User) =>
      aUser.active && !bUser.active ? -1 : 1
    )
    .sort(sortByActiveAndName)
    .map(user => {
      return {
        name: `${user.firstName.trim()} ${user.lastName.trim()}`,
        userName: user.userName,
        email: user.email,
        market: getUserMarketsString(user.markets, user.defaultMarketId),
        role: t(enumToTranslationText(Roles[user.role].toString()), {
          ns: 'user'
        }),
        editRights: t(enumToTranslationText(EditRights[user.editRights]), {
          ns: 'user'
        }),
        status: user.active ? t('active') : t('inactive'),
        userId: user.userId
      }
    })

  return preparedData
}

export function sortByActiveAndName(
  { firstName: aFirstName, lastName: aLastName, active: aActive }: User,
  { firstName: bFirstName, lastName: bLastName, active: bActive }: User
) {
  return aActive === bActive
    ? `${aFirstName.trim()} ${aLastName.trim()}`.localeCompare(
        `${bFirstName.trim()} ${bLastName.trim()}`
      )
    : aActive && !bActive
    ? -1
    : 1
}
